import { format } from "date-fns";

import { dateFormat } from "./constants";

const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * Format a `Date` (or epoch millis) using `date-fns`. When `isSGTTimezone` is
 * true the input is shifted by +8h before formatting — Singapore has no DST
 * so the constant offset is safe. Result is memoized by
 * `${date.valueOf()}-${template}-${isSGTTimezone}`.
 *
 */
export function formatDate(
  date?: Date | number,
  template: string = dateFormat,
  isSGTTimezone: boolean = false,
): string {
  if (!date) return "";

  const cacheKey = `${date.valueOf()}-${template}-${isSGTTimezone}`;
  if (formatDate.cache[cacheKey]) {
    return formatDate.cache[cacheKey];
  }

  if (isSGTTimezone) {
    const utcDate = new Date(date);
    const sgtDate = new Date(utcDate.getTime() + SGT_OFFSET_MS);
    return (formatDate.cache[cacheKey] = format(sgtDate, template));
  }

  formatDate.cache[cacheKey] = format(date, template);
  return formatDate.cache[cacheKey];
}
formatDate.cache = {} as Record<string, string>;

/**
 * Format a UTC DateTime string (as returned by the API, with no offset) in
 * Singapore time. `new Date` reads the offsetless value as local time, so
 * {@link formatDate}'s +8h shift lands on SGT in any browser timezone.
 * Empty or unparseable input returns `fallback`.
 */
export function formatSGTDateTime(
  value?: string | null,
  template: string = dateFormat,
  fallback: string = "",
): string {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return fallback;
  return formatDate(parsed, template, true);
}
